import { useState, useCallback, useEffect, useRef } from 'react';
import type { ConnectionState, TranscriptMessage } from '../types';
import {
  processAudioPayload,
  float32ToInt16,
  calculateCallDuration,
  encodeWAV,
} from '../lib/audio-utils';
import { initCallSession, finalizeCallSession, uploadRecording, type CallSessionResponse } from '../lib/call-api';

/**
 * WebSocket call hook options
 */
export interface UseWebSocketCallOptions {
  wsUrl?: string;
  assistantId: string;
  apiKey?: string;
  userId?: string;
  workspaceId?: string;
  apiBaseUrl?: string;
  language?: string;
  voiceModal?: string;
  voiceName?: string;
  autoConnect?: boolean;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
}

export interface UseWebSocketCallReturn {
  connectionState: ConnectionState;
  connect: () => Promise<void>;
  disconnect: () => void;
  isAudioMuted: boolean;
  toggleAudio: () => void;
  error: Error | null;
  transcript: TranscriptMessage[];
  clearTranscript: () => void;
}

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Hook for managing WebSocket-based voice calls
 * Matches the qcallai-app RolePlayAssistant flow exactly:
 * 1. Create AudioContext (8kHz) - BEFORE WebSocket
 * 2. initCallSession -> POST /create-temp-call-data
 * 3. WebSocket connect -> send start event with session data
 * 4. Start microphone capture using the SAME AudioContext
 * 5. Incoming audio plays back through the SAME AudioContext
 * 6. On disconnect -> finalize session
 */
export function useWebSocketCall(options: UseWebSocketCallOptions): UseWebSocketCallReturn {
  const {
    wsUrl = 'wss://api.qcall.ai/connection',
    assistantId,
    apiKey,
    userId,
    workspaceId,
    apiBaseUrl = 'https://api.qcall.ai/api/v1',
    language,
    voiceModal,
    voiceName,
    autoConnect = false,
    onConnect,
    onDisconnect,
    onError,
  } = options;

  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [transcript, setTranscript] = useState<TranscriptMessage[]>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const callStartTimeRef = useRef<number>(0);
  const callSidRef = useRef<string>('');
  const tempCallHistoryIdRef = useRef<string>('');
  const sessionResponseRef = useRef<CallSessionResponse | null>(null);
  const isMutedRef = useRef(false);
  const scheduledAudioRef = useRef<AudioBufferSourceNode[]>([]);
  const nextPlayTimeRef = useRef<number>(0);
  const connectionStateRef = useRef<ConnectionState>('idle');
  const recordingDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const recordingProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const recordingPcmChunksRef = useRef<Int16Array[]>([]);
  const isDisconnectingRef = useRef(false);

  connectionStateRef.current = connectionState;

  const addTranscriptMessage = useCallback((speaker: 'user' | 'agent', text: string) => {
    const message: TranscriptMessage = {
      id: generateUUID(),
      speaker,
      text,
      timestamp: new Date(),
    };
    setTranscript((prev) => [...prev, message]);
  }, []);

  const clearTranscript = useCallback(() => {
    setTranscript([]);
  }, []);

  /**
   * Process audio chunk from server - matches qcallai-app processAudioChunk exactly
   * Uses audioContextRef which is created BEFORE WebSocket connects
   */
  const processAudioChunk = useCallback((base64Payload: string) => {
    const context = audioContextRef.current;
    if (!context) return;

    if (context.state === 'suspended') {
      context.resume();
    }

    try {
      // Decode base64 µ-law to Int16 PCM
      const pcmData = processAudioPayload(base64Payload);
      const int16Data = new Int16Array(pcmData);

      // Convert Int16 to Float32 (matches qcallai-app: / 32768.0)
      const float32Data = new Float32Array(int16Data.length);
      for (let i = 0; i < int16Data.length; i++) {
        float32Data[i] = int16Data[i] / 32768.0;
      }

      // Create AudioBuffer at 8kHz (matches qcallai-app exactly)
      const audioBuffer = context.createBuffer(1, float32Data.length, 8000);
      audioBuffer.getChannelData(0).set(float32Data);

      const source = context.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(context.destination);
      // Connect to recording destination for mixed recording
      if (recordingDestRef.current) {
        source.connect(recordingDestRef.current);
      }

      // Schedule playback with precise timing (matches qcallai-app)
      const currentTime = context.currentTime;
      let startTime = nextPlayTimeRef.current;
      if (startTime < currentTime) {
        startTime = currentTime;
      }

      source.start(startTime);
      nextPlayTimeRef.current = startTime + audioBuffer.duration;

      scheduledAudioRef.current.push(source);

      source.onended = () => {
        scheduledAudioRef.current = scheduledAudioRef.current.filter(
          (s) => s !== source
        );
      };
    } catch (e) {
      console.error('Error processing audio chunk:', e);
    }
  }, []);

  const sendAudioData = useCallback((pcmData: Int16Array) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && !isMutedRef.current) {
      wsRef.current.send(pcmData.buffer as ArrayBuffer);
    }
  }, []);

  const startMicrophoneCapture = useCallback(async () => {
    try {
      // qcallai-app uses simple { audio: true }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const context = audioContextRef.current;
      if (!context) return;

      const source = context.createMediaStreamSource(stream);
      const processor = context.createScriptProcessor(4096, 1, 1);
      scriptProcessorRef.current = processor;

      // Connect mic to recording destination for mixed recording
      if (recordingDestRef.current) {
        source.connect(recordingDestRef.current);
      }

      processor.onaudioprocess = (e) => {
        const inputData = e.inputBuffer.getChannelData(0);
        // Convert Float32 to Int16 PCM (matches qcallai-app)
        const pcmData = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        sendAudioData(pcmData);
      };

      source.connect(processor);
      // Must connect processor to destination for onaudioprocess to fire reliably
      // Use GainNode(gain=0) to prevent echo (mic audio won't play through speakers)
      const silentGain = context.createGain();
      silentGain.gain.value = 0;
      processor.connect(silentGain);
      silentGain.connect(context.destination);
    } catch (err) {
      let errorMsg = 'Failed to start microphone';
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          const msg = err.message?.toLowerCase() || '';
          if (
            msg.includes('permission policy') ||
            msg.includes('permissions policy') ||
            msg.includes('not allowed') ||
            msg.includes('disabled by') ||
            msg.includes('blocked by')
          ) {
            errorMsg = 'Microphone access is blocked by this website\'s security policy. Please ask the site owner to update the Permissions-Policy header to allow microphone access (microphone=(self)).';
          } else {
            errorMsg = 'Microphone permission denied. Please allow microphone access in your browser settings and try again.';
          }
        } else if (err.name === 'NotFoundError') {
          errorMsg = 'No microphone found. Please connect a microphone and try again.';
        }
      }
      const error = new Error(errorMsg);
      setError(error);
      setConnectionState('error');
      onError?.(error);
    }
  }, [sendAudioData, onError]);

  const stopMicrophoneCapture = useCallback(() => {
    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  const cleanupAudioResources = useCallback(() => {
    scheduledAudioRef.current.forEach((source) => {
      try { source.stop(); } catch { /* ignore */ }
    });
    scheduledAudioRef.current = [];
    nextPlayTimeRef.current = 0;
  }, []);

  const closeAudioContext = useCallback(() => {
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, []);

  /**
   * Check if microphone is available (not blocked by Permissions-Policy)
   */
  const checkMicrophoneAvailability = useCallback(async (): Promise<string | null> => {
    // Check if mediaDevices API is available (requires secure context)
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return 'Microphone access is not available. This site may not be using HTTPS.';
    }

    try {
      // Try a quick getUserMedia to check if mic is blocked by Permissions-Policy
      const testStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Immediately stop - we just wanted to check availability
      testStream.getTracks().forEach((track) => track.stop());
      return null; // Mic is available
    } catch (err) {
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          // Check if this is a Permissions-Policy block (not a user denial)
          const msg = err.message?.toLowerCase() || '';
          if (
            msg.includes('permission policy') ||
            msg.includes('permissions policy') ||
            msg.includes('not allowed') ||
            msg.includes('disabled by') ||
            msg.includes('blocked by')
          ) {
            return 'Microphone access is blocked by this website\'s security policy. Please ask the site owner to update the Permissions-Policy header to allow microphone access (microphone=(self)).';
          }
          // User denied the permission prompt - different message
          return 'Microphone permission denied. Please allow microphone access in your browser settings and try again.';
        }
        if (err.name === 'NotFoundError') {
          return 'No microphone found. Please connect a microphone and try again.';
        }
      }
      return 'Unable to access microphone. Please check your browser settings.';
    }
  }, []);

  /**
   * Connect - matches qcallai-app RolePlayAssistant flow exactly:
   * 1. Check microphone availability FIRST
   * 2. Create AudioContext (so it's ready for incoming audio)
   * 3. POST /create-temp-call-data -> get session data
   * 4. WebSocket connect -> send start event
   * 5. Start microphone using the SAME AudioContext
   */
  const connect = useCallback(async () => {
    if (connectionState !== 'idle') return;

    try {
      setConnectionState('connecting');
      connectionStateRef.current = 'connecting';
      setError(null);
      callStartTimeRef.current = Date.now();

      // Step 0: Check microphone availability before doing anything else
      const micError = await checkMicrophoneAvailability();
      if (micError) {
        const error = new Error(micError);
        setError(error);
        // Reset to idle so user can retry (keep error message visible)
        setConnectionState('idle');
        connectionStateRef.current = 'idle';
        onError?.(error);
        return;
      }

      // Step 1: Create AudioContext (matches qcallai-app - created before WebSocket)
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({
          sampleRate: 8000,
        });
      }
      // Resume if suspended (browser autoplay policy)
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      // Set up recording: capture both mic + AI output as mixed PCM via MediaStreamDestination
      const recordingDest = audioContextRef.current.createMediaStreamDestination();
      recordingDestRef.current = recordingDest;
      const recordingSource = audioContextRef.current.createMediaStreamSource(recordingDest.stream);
      const recordingProcessor = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      recordingProcessorRef.current = recordingProcessor;
      recordingPcmChunksRef.current = [];

      recordingProcessor.onaudioprocess = (e) => {
        const inputData = e.inputBuffer.getChannelData(0);
        const pcmData = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        recordingPcmChunksRef.current.push(pcmData);
        // Silence output to prevent feedback
        const outputData = e.outputBuffer.getChannelData(0);
        for (let i = 0; i < outputData.length; i++) {
          outputData[i] = 0;
        }
      };
      recordingSource.connect(recordingProcessor);
      recordingProcessor.connect(audioContextRef.current.destination);

      // Step 2: Initialize call session (same as qcallai-app getCallHistoryId) — only reached if mic is available
      const sessionResponse = await initCallSession({
        assistant_id: assistantId,
        userId,
        workspace_id: workspaceId,
      }, apiKey, apiBaseUrl);

      sessionResponseRef.current = sessionResponse;
      tempCallHistoryIdRef.current = sessionResponse.temp_call_history_id;
      callSidRef.current = sessionResponse.uuid;

      // Step 3: Create WebSocket connection
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        // Step 4: Send start event - EXACT same format as qcallai-app
        const streamSid = sessionResponse.uuid;
        ws.send(JSON.stringify({
          event: 'start',
          start: {
            callSid: streamSid,
            streamSid: streamSid,
            customParameters: {
              temp_call_history_id: sessionResponse.temp_call_history_id,
              language: sessionResponse.language,
              voice_modal: sessionResponse.voice_modal,
              start_speech: sessionResponse.start_speech,
              voice_name: sessionResponse.voice_name,
              is_back_sound: sessionResponse.is_back_sound,
              voice_speed: sessionResponse.voice_speed,
              recorded_audio: sessionResponse.recorded_audio,
              calling_type: 'web',
              mode: "widget",
              is_wait_start_speech: sessionResponse.is_wait_start_speech ?? true,
            },
          },
        }));

        setConnectionState('connected');
        connectionStateRef.current = 'connected';
        onConnect?.();

        // Step 5: Start microphone capture using the SAME AudioContext
        startMicrophoneCapture();
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          if (typeof event.data === 'string') {
            const data = JSON.parse(event.data);

            // Handle Deepgram transcripts
            if (data.channel?.alternatives?.[0]?.transcript) {
              // Deepgram interim transcript - not stored
            }

            switch (data.event) {
              case 'media':
                if (data.media?.payload) {
                  processAudioChunk(data.media.payload);
                }
                break;

              case 'transcript':
                if (data.role && data.text) {
                  addTranscriptMessage(
                    data.role === 'user' ? 'user' : 'agent',
                    data.text
                  );
                }
                break;

              case 'clear':
                cleanupAudioResources();
                break;

              case 'stop':
                // Call ended by server
                doDisconnect();
                break;
            }
          }
        } catch (err) {
          console.error('Error handling WebSocket message:', err);
        }
      };

      ws.onerror = () => {
        const error = new Error('WebSocket connection error');
        setError(error);
        setConnectionState('error');
        connectionStateRef.current = 'error';
        onError?.(error);
      };

      ws.onclose = () => {
        if (connectionStateRef.current === 'connected' || connectionStateRef.current === 'connecting') {
          doDisconnect();
        }
      };

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to connect');
      setError(error);
      setConnectionState('error');
      connectionStateRef.current = 'error';
      onError?.(error);
    }
  }, [
    connectionState, assistantId, apiKey, userId, workspaceId,
    apiBaseUrl, wsUrl, onConnect, onError,
    checkMicrophoneAvailability,
    startMicrophoneCapture, processAudioChunk, addTranscriptMessage, cleanupAudioResources,
  ]);

  /**
   * Internal disconnect - handles cleanup and session finalization
   */
  const doDisconnect = useCallback(async () => {
    // Guard against double disconnect (user click + ws.onclose)
    if (isDisconnectingRef.current) return;
    isDisconnectingRef.current = true;

    stopMicrophoneCapture();
    cleanupAudioResources();

    // Send stop event and close WebSocket
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          streamSid: callSidRef.current,
          event: 'stop',
        }));
      }
      wsRef.current.close();
      wsRef.current = null;
    }

    // Calculate call duration
    const callDuration = calculateCallDuration(callStartTimeRef.current, Date.now());

    // Upload recording and finalize session
    if (callDuration > 0 && callSidRef.current) {
      // Stop recording processor
      if (recordingProcessorRef.current) {
        recordingProcessorRef.current.disconnect();
        recordingProcessorRef.current = null;
      }

      let recordingUrl = '';

      // Encode accumulated PCM to WAV and upload
      if (recordingPcmChunksRef.current.length > 0) {
        try {
          const wavBlob = encodeWAV(recordingPcmChunksRef.current);
          recordingUrl = await uploadRecording(wavBlob, callSidRef.current, apiKey, apiBaseUrl);
        } catch (err) {
          console.error('Error uploading recording:', err);
        }
      }

      try {
        await finalizeCallSession({
          call_sid: callSidRef.current,
          call_duration: callDuration,
          recording_url: recordingUrl,
          userId,
          temp_call_history_id: tempCallHistoryIdRef.current,
        }, apiKey, apiBaseUrl);
      } catch (err) {
        console.error('Error finalizing call:', err);
      }
    }

    // Clear buffers and transcript
    recordingPcmChunksRef.current = [];
    recordingDestRef.current = null;
    sessionResponseRef.current = null;
    setTranscript([]);

    setConnectionState('disconnected');
    connectionStateRef.current = 'disconnected';
    onDisconnect?.();

    // Close audio context after short delay
    setTimeout(() => {
      closeAudioContext();
      setConnectionState('idle');
      connectionStateRef.current = 'idle';
      isDisconnectingRef.current = false;
    }, 500);
  }, [stopMicrophoneCapture, cleanupAudioResources, closeAudioContext, userId, apiKey, apiBaseUrl, onDisconnect]);

  /**
   * Public disconnect - called by UI
   */
  const disconnect = useCallback(() => {
    doDisconnect();
  }, [doDisconnect]);

  const toggleAudio = useCallback(() => {
    isMutedRef.current = !isMutedRef.current;
    setIsAudioMuted(isMutedRef.current);
  }, []);

  useEffect(() => {
    if (autoConnect && connectionState === 'idle') {
      connect();
    }
  }, [autoConnect, connectionState, connect]);

  useEffect(() => {
    return () => {
      stopMicrophoneCapture();
      cleanupAudioResources();
      closeAudioContext();
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [stopMicrophoneCapture, cleanupAudioResources, closeAudioContext]);

  return {
    connectionState,
    connect,
    disconnect,
    isAudioMuted,
    toggleAudio,
    error,
    transcript,
    clearTranscript,
  };
}
